import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {
  Tabs,
  Button,
  Input,
  Dropdown,
  Menu,
  Modal,
  message,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EllipsisOutlined,
  FilterOutlined
} from '@ant-design/icons';
import {FilterRule} from '../../types';
import '../../style/ViewTabs.css';

export interface ViewConfig {
  id: string;
  name: string;
  filters: FilterRule[];
  logicRelation: 'and' | 'or';
  isDefault?: boolean;
}

interface ViewTabsProps {
  views: ViewConfig[];
  activeViewId: string;
  onViewChange: (viewId: string) => void;
  onViewCreate: (
    name: string,
    filters: FilterRule[],
    logicRelation: 'and' | 'or'
  ) => void;
  onViewUpdate: (
    viewId: string,
    name: string,
    filters: FilterRule[],
    logicRelation: 'and' | 'or'
  ) => void;
  onViewDelete: (viewId: string) => void;
  onViewRename: (viewId: string, newName: string) => void;
  currentFilters: FilterRule[];
  currentLogicRelation: 'and' | 'or';
  children: React.ReactNode;
}

export const ViewTabs: React.FC<ViewTabsProps> = ({
  views,
  activeViewId,
  onViewChange,
  onViewCreate,
  onViewUpdate,
  onViewDelete,
  onViewRename,
  currentFilters,
  currentLogicRelation,
  children
}) => {
  const [isCreatingView, setIsCreatingView] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [renamingViewId, setRenamingViewId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // 创建新视图
  const handleCreateView = useCallback(() => {
    if (!newViewName.trim()) {
      message.error('视图名称不能为空');
      return;
    }

    onViewCreate(newViewName.trim(), currentFilters, currentLogicRelation);
    setNewViewName('');
    setIsCreatingView(false);
  }, [newViewName, currentFilters, currentLogicRelation, onViewCreate]);

  // 获取视图的筛选条件摘要 - 使用 useMemo 缓存
  const getFilterSummary = useCallback((view: ViewConfig) => {
    if (view.filters.length === 0) return '无筛选条件';

    const summary = view.filters
      .map(f => `${f.field} ${f.operator} ${f.value}`)
      .join(`, ${view.logicRelation === 'or' ? '或' : '且'} `);
    return summary.length > 30 ? summary.substring(0, 30) + '...' : summary;
  }, []);

  // 重命名视图
  const handleRename = useCallback(
    (viewId: string) => {
      if (!renameValue.trim()) {
        message.error('视图名称不能为空');
        return;
      }

      onViewRename(viewId, renameValue.trim());
      setRenamingViewId(null);
      setRenameValue('');
    },
    [renameValue, onViewRename]
  );

  // 删除视图
  const handleDelete = useCallback(
    (viewId: string) => {
      Modal.confirm({
        title: '确认删除',
        content: '确定要删除这个视图吗？',
        onOk: () => {
          onViewDelete(viewId);
        }
      });
    },
    [onViewDelete]
  );

  // 处理重命名输入
  const handleRenameInput = useCallback(
    (viewId: string, event: React.KeyboardEvent | React.FocusEvent) => {
      event.stopPropagation();
      if (
        event.type === 'blur' ||
        (event.type === 'keydown' &&
          (event as React.KeyboardEvent).key === 'Enter')
      ) {
        handleRename(viewId);
      }
    },
    [handleRename]
  );

  // 渲染操作菜单
  const renderTabMenu = useCallback(
    (view: ViewConfig, event?: React.MouseEvent) => {
      if (event) {
        event.stopPropagation(); // 阻止事件冒泡
      }

      if (view.isDefault) return null;

      const menu = (
        <Menu>
          <Menu.Item
            key="rename"
            onClick={e => {
              e.domEvent.stopPropagation();
              setRenamingViewId(view.id);
              setRenameValue(view.name);
            }}
          >
            重命名
          </Menu.Item>
          <Menu.Item
            key="delete"
            danger
            onClick={e => {
              e.domEvent.stopPropagation();
              handleDelete(view.id);
            }}
          >
            删除
          </Menu.Item>
        </Menu>
      );

      return (
        <Dropdown
          overlay={menu}
          trigger={['click']}
          placement="bottomRight"
          onVisibleChange={visible => {
            if (!visible) {
              // 当下拉菜单关闭时，不影响标签页切换
            }
          }}
        >
          <span onClick={e => e.stopPropagation()}>
            <EllipsisOutlined />
          </span>
        </Dropdown>
      );
    },
    [handleDelete]
  );

  // 保存当前筛选为视图
  const handleSaveCurrentFilters = useCallback(() => {
    if (currentFilters.length === 0) {
      message.warning('当前没有筛选条件，无需保存');
      return;
    }

    const activeView = views.find(v => v.id === activeViewId);
    if (activeView && !activeView.isDefault) {
      // 更新现有视图
      onViewUpdate(
        activeViewId,
        activeView.name,
        currentFilters,
        currentLogicRelation
      );
      message.success('视图已更新');
    } else {
      // 创建新视图
      setIsCreatingView(true);
    }
  }, [activeViewId, views, currentFilters, currentLogicRelation, onViewUpdate]);

  // 检查当前筛选是否有变化 - 使用 useMemo 优化
  const hasFilterChanges = useMemo(() => {
    const activeView = views.find(v => v.id === activeViewId);
    if (!activeView) return false;

    if (activeView.isDefault) {
      // 对于默认视图，只要有筛选条件就算有变化
      return currentFilters.length > 0;
    }

    // 对于自定义视图，比较当前筛选条件与视图保存的条件是否不同
    const currentFiltersStr = JSON.stringify(
      currentFilters.map(f => ({
        field: f.field,
        operator: f.operator,
        value: f.value
      }))
    );
    const savedFiltersStr = JSON.stringify(
      activeView.filters.map(f => ({
        field: f.field,
        operator: f.operator,
        value: f.value
      }))
    );

    const filtersChanged = currentFiltersStr !== savedFiltersStr;
    const logicChanged = currentLogicRelation !== activeView.logicRelation;

    return filtersChanged || logicChanged;
  }, [views, activeViewId, currentFilters, currentLogicRelation]);

  const renderTabTitle = useCallback(
    (view: ViewConfig) => {
      if (renamingViewId === view.id) {
        return (
          <div
            style={{display: 'flex', alignItems: 'center'}}
            onClick={e => e.stopPropagation()}
          >
            <Input
              size="small"
              value={renameValue}
              onChange={e => {
                e.stopPropagation();
                setRenameValue(e.target.value);
              }}
              onKeyDown={e => handleRenameInput(view.id, e)}
              onBlur={e => handleRenameInput(view.id, e)}
              style={{width: 100}}
              autoFocus
            />
          </div>
        );
      }

      const isActive = view.id === activeViewId;

      return (
        <div style={{display: 'flex', alignItems: 'center'}}>
          <span>{view.name}</span>

          {/* 对于活动视图，显示当前筛选条件 */}
          {isActive && currentFilters.length > 0 && (
            <Tooltip
              title={`当前筛选: ${currentFilters.length} 个条件 (${
                currentLogicRelation === 'or' ? '任意' : '所有'
              })`}
            >
              <FilterOutlined
                style={{marginLeft: 4, fontSize: 12, color: '#1677FF'}}
              />
            </Tooltip>
          )}

          {/* 对于非活动视图，显示保存的筛选条件 */}
          {!isActive && view.filters.length > 0 && (
            <Tooltip title={getFilterSummary(view)}>
              <FilterOutlined
                style={{marginLeft: 4, fontSize: 12, color: '#52c41a'}}
              />
            </Tooltip>
          )}

          {/* 显示未保存标识 - 改进逻辑 */}
          {hasFilterChanges && isActive && (
            <Tooltip
              title={
                view.isDefault
                  ? '有新的筛选条件，可保存为新视图'
                  : '筛选条件已修改，点击保存按钮更新视图'
              }
            >
              <span
                style={{color: '#ff4d4f', marginLeft: 4, fontWeight: 'bold'}}
              >
                *
              </span>
            </Tooltip>
          )}

          {/* 操作菜单 */}
          <div onClick={e => e.stopPropagation()}>{renderTabMenu(view)}</div>
        </div>
      );
    },
    [
      renamingViewId,
      renameValue,
      activeViewId,
      currentFilters,
      currentLogicRelation,
      hasFilterChanges,
      getFilterSummary,
      renderTabMenu,
      handleRenameInput
    ]
  );

  // 改进顶部操作栏的显示逻辑
  const renderTopActionBar = useCallback(() => {
    if (!hasFilterChanges) return null;

    const activeView = views.find(v => v.id === activeViewId);
    const isDefaultView = activeView?.isDefault;

    return (
      <div
        style={{
          padding: '8px 16px',
          // borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
          // backgroundColor: isDefaultView ? '#fff7e6' : '#f6ffed' // 不同背景色区分默认视图和自定义视图
          backgroundColor: isDefaultView ? '#F0F2F5' : '#f6ffed' // 不同背景色区分默认视图和自定义视图
        }}
      >
        <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
          <Button
            type="primary"
            size="small"
            onClick={handleSaveCurrentFilters}
            icon={isDefaultView ? <PlusOutlined /> : undefined}
          >
            {isDefaultView ? '保存为新视图' : '保存筛选'}
          </Button>

          {!isDefaultView && (
            <span style={{fontSize: 12, color: '#666', marginLeft: 8}}>
              筛选条件已修改
            </span>
          )}
        </div>

        {currentFilters.length > 0 && (
          <div style={{fontSize: 12, color: '#666'}}>
            <FilterOutlined style={{marginRight: 4}} />
            {currentFilters.length} 个筛选条件 (
            {currentLogicRelation === 'or' ? '任意' : '所有'})
          </div>
        )}
      </div>
    );
  }, [
    hasFilterChanges,
    views,
    activeViewId,
    currentFilters,
    currentLogicRelation,
    handleSaveCurrentFilters
  ]);

  // 构建标签页数据 - 使用 Antd 4.x+ 语法
  const tabItems = useMemo(() => {
    return views.map(view => {
      const isActive = view.id === activeViewId;
      const isRenaming = renamingViewId === view.id;

      // 构建标签标题
      let tabLabel: React.ReactNode;

      if (isRenaming) {
        // 重命名状态
        tabLabel = (
          <div
            style={{display: 'flex', alignItems: 'center'}}
            onClick={e => e.stopPropagation()}
          >
            <Input
              size="small"
              value={renameValue}
              onChange={e => {
                e.stopPropagation();
                setRenameValue(e.target.value);
              }}
              onKeyDown={e => handleRenameInput(view.id, e)}
              onBlur={e => handleRenameInput(view.id, e)}
              style={{width: 100}}
              autoFocus
            />
          </div>
        );
      } else {
        // 正常显示状态
        tabLabel = (
          <div style={{display: 'flex', alignItems: 'center'}}>
            <span>{view.name}</span>

            {/* 对于活动视图，显示当前筛选条件 */}
            {isActive && currentFilters.length > 0 && (
              <Tooltip
                title={`当前筛选: ${currentFilters.length} 个条件 (${
                  currentLogicRelation === 'or' ? '任意' : '所有'
                })`}
              >
                <FilterOutlined
                  style={{marginLeft: 4, fontSize: 12, color: '#1677FF'}}
                />
              </Tooltip>
            )}

            {/* 对于非活动视图，显示保存的筛选条件 */}
            {!isActive && view.filters.length > 0 && (
              <Tooltip title={getFilterSummary(view)}>
                <FilterOutlined
                  style={{marginLeft: 4, fontSize: 12, color: '#52c41a'}}
                />
              </Tooltip>
            )}

            {/* 显示未保存标识 */}
            {hasFilterChanges && isActive && (
              <span style={{color: '#ff4d4f', marginLeft: 4}}>*</span>
            )}

            {/* 操作菜单 */}
            <div onClick={e => e.stopPropagation()}>{renderTabMenu(view)}</div>
          </div>
        );
      }

      return {
        key: view.id,
        label: tabLabel,
        children: null // 内容在外部渲染
      };
    });
  }, [
    views,
    activeViewId,
    renamingViewId,
    renameValue,
    currentFilters,
    currentLogicRelation,
    hasFilterChanges,
    getFilterSummary,
    renderTabMenu,
    handleRenameInput
  ]);

  return (
    <div
      style={{
        // background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 8,
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* 顶部操作栏 */}
      {renderTopActionBar()}

      {/* 标签页 */}
      <div
        style={{
          flexShrink: 0
        }}
        className="custom-view-tabs"
      >
        <Tabs
          activeKey={activeViewId}
          onChange={onViewChange}
          type="card"
          size="small"
          items={tabItems}
          tabBarStyle={{
            margin: 0,
            borderBottom: 'none'
          }}
        />
      </div>

      {/* 内容区域 */}
      <div style={{flex: 1, overflow: 'hidden'}}>{children}</div>

      {/* 创建视图弹窗 */}
      <Modal
        title="创建新视图"
        open={isCreatingView}
        onOk={handleCreateView}
        onCancel={() => {
          setIsCreatingView(false);
          setNewViewName('');
        }}
        okText="创建"
        cancelText="取消"
      >
        <div style={{marginBottom: 16}}>
          <label style={{display: 'block', marginBottom: 8}}>视图名称</label>
          <Input
            placeholder="输入视图名称"
            value={newViewName}
            onChange={e => setNewViewName(e.target.value)}
            onPressEnter={handleCreateView}
            autoFocus
          />
        </div>

        {currentFilters.length > 0 && (
          <div>
            <label style={{display: 'block', marginBottom: 8}}>
              当前筛选条件
            </label>
            <div
              style={{
                background: '#f5f5f5',
                padding: 12,
                borderRadius: 4,
                fontSize: 12,
                color: '#666'
              }}
            >
              {getFilterSummary({
                id: '',
                name: '',
                filters: currentFilters,
                logicRelation: currentLogicRelation
              })}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
